import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import {
  CalendarDays,
  Store,
  MapPin,
  Receipt as ReceiptIcon,
  RefreshCw,
  Trash2,
  Download,
  FileJson,
  FileSpreadsheet,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { isInsufficientCreditsError } from "@/lib/edgeFunctionErrors";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { exportReceiptAsCSV, exportReceiptAsJSON } from "@/lib/exportReceipt";
import * as Sentry from "@sentry/react";

interface ReceiptItem {
  name: string;
  amount: number;
  category: "food" | "household" | "health" | "personal" | "other";
  quantity: number;
  price: number;
}

interface Receipt {
  id: string; // receipts.id is a UUID
  date: string;
  store: string;
  total: number;
  items: ReceiptItem[];
  imageUrl: string;
  address?: string;
  currency?: string;
  subtotal?: number;
  store_id?: string | null;
}

interface ReceiptDetailsContentProps {
  receipt: Receipt;
  onReanalyze?: () => void;
  onDelete?: () => void;
}

const categoryColors = {
  food: "bg-category-food/20 text-category-food border-category-food/30",
  household: "bg-category-household/20 text-category-household border-category-household/30",
  health: "bg-category-health/20 text-category-health border-category-health/30",
  personal: "bg-category-personal/20 text-category-personal border-category-personal/30",
  other: "bg-category-other/20 text-category-other border-category-other/30",
};

const categoryIcons = {
  food: "🍎",
  household: "🏠",
  health: "💊",
  personal: "🧴",
  other: "📦",
};

export const ReceiptDetailsContent = ({ receipt, onReanalyze, onDelete }: ReceiptDetailsContentProps) => {
  const { t } = useTranslation();
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const navigate = useNavigate();
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleReanalyze = async () => {
    if (!receipt.imageUrl) {
      toast.error(t('receiptDetails.noImageError'));
      return;
    }

    setIsReanalyzing(true);
    try {
      const { data, error } = await supabase.functions.invoke('analyze-receipt', {
        body: {
          receiptId: receipt.id,
          imageUrl: receipt.imageUrl
        }
      });

      if (error) throw error;

      // The credit is debited server-side by the analyze-receipt function; we
      // only refresh the balance display here.
      window.dispatchEvent(new Event('creditsUpdated'));

      toast.success(t('receiptDetails.reanalyzeSuccess'));
      if (onReanalyze) onReanalyze();
    } catch (error) {
      // The server-side credit gate answered 402. That is an expected outcome,
      // not a failure: tell the user they're out of credits, and refresh the
      // balance display so the rest of the UI catches up.
      if (await isInsufficientCreditsError(error)) {
        window.dispatchEvent(new Event('creditsUpdated'));
        toast.error(t('uploadArea.insufficientBalance'), {
          description: t('uploadArea.insufficientBalanceDescription'),
        });
        return;
      }

      console.error('Error re-analyzing receipt:', error);

      const sentryEventId = Sentry.captureException(error, {
        level: 'error',
        tags: {
          component: 'ReceiptDetailsContent',
          action: 'reanalyze_receipt',
          error_code: '1002', // RECEIPT_REANALYZE_FAILED
        },
        extra: {
          receiptId: receipt.id,
          receiptStore: receipt.store,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      });

      const supportId = sentryEventId
        ? sentryEventId.substring(0, 8).toUpperCase()
        : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;

      toast.error(t('receiptDetails.reanalyzeErrorWithSupport', { supportId }), {
        duration: Infinity,
      });
    } finally {
      setIsReanalyzing(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      // Delete line items first
      const { error: lineItemsError } = await supabase
        .from('line_items')
        .delete()
        .eq('receipt_id', String(receipt.id));

      if (lineItemsError) throw lineItemsError;

      // Delete receipt
      const { error: receiptError } = await supabase
        .from('receipts')
        .delete()
        .eq('id', String(receipt.id));

      if (receiptError) throw receiptError;

      toast.success(t('receiptDetails.deleteSuccess'));
      setShowDeleteDialog(false);
      if (onDelete) onDelete();
    } catch (error) {
      console.error('Error deleting receipt:', error);

      const sentryEventId = Sentry.captureException(error, {
        level: 'error',
        tags: {
          component: 'ReceiptDetailsContent',
          action: 'delete_receipt',
          error_code: '1003', // RECEIPT_DELETE_FAILED
        },
        extra: {
          receiptId: receipt.id,
          receiptStore: receipt.store,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      });

      const supportId = sentryEventId
        ? sentryEventId.substring(0, 8).toUpperCase()
        : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;

      toast.error(t('receiptDetails.deleteErrorWithSupport', { supportId }), {
        duration: Infinity,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportJSON = () => {
    try {
      exportReceiptAsJSON(receipt);
      toast.success("Receipt exported as JSON!");
      setShowExportMenu(false);
    } catch (error) {
      console.error('Error exporting receipt as JSON:', error);
      toast.error("Failed to export receipt as JSON");
    }
  };

  const handleExportCSV = () => {
    try {
      exportReceiptAsCSV(receipt);
      toast.success("Receipt exported as CSV!");
      setShowExportMenu(false);
    } catch (error) {
      console.error('Error exporting receipt as CSV:', error);
      toast.error("Failed to export receipt as CSV");
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="pb-2 border-b shrink-0">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <ReceiptIcon className="h-6 w-6 text-primary" />
          {t('receiptDetails.title')}
        </h2>
      </div>
      
      <ScrollArea className="flex-1 pr-4 mt-4">
        <div className="space-y-6">
        {/* Receipt Metadata */}
        <Card className="shadow-custom-md border-0">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-3 flex-1">
                <div className="flex items-center gap-2 text-xl font-semibold">
                  <Store className="h-5 w-5 text-primary" />
                  {receipt.store_id ? (
                    <button
                      onClick={() => navigate(`/stores/${receipt.store_id}`)}
                      className="hover:underline text-left"
                    >
                      {receipt.store}
                    </button>
                  ) : (
                    <span>{receipt.store}</span>
                  )}
                </div>
                
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CalendarDays className="h-4 w-4" />
                  {formatDate(receipt.date)}
                </div>

                {receipt.address && (
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{receipt.address}</span>
                  </div>
                )}
              </div>

              <div className="text-right space-y-3">
                <div className="flex items-center gap-2 justify-end">
                  <div className="relative">
                    <Button
                      onClick={() => setShowExportMenu(!showExportMenu)}
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      title="Export receipt"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    {showExportMenu && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setShowExportMenu(false)}
                        />
                        <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-popover border z-20">
                          <div className="py-1">
                            <button
                              onClick={handleExportJSON}
                              className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-accent transition-colors"
                            >
                              <FileJson className="h-4 w-4" />
                              Export as JSON
                            </button>
                            <button
                              onClick={handleExportCSV}
                              className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-accent transition-colors"
                            >
                              <FileSpreadsheet className="h-4 w-4" />
                              Export as CSV
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <Button
                    onClick={handleReanalyze}
                    disabled={isReanalyzing}
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    title={t('receiptDetails.reanalyzeButton')}
                  >
                    <RefreshCw className={cn("h-4 w-4", isReanalyzing && "animate-spin")} />
                  </Button>
                  <Button
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={isDeleting}
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    title={t('receiptDetails.deleteButton')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground mb-1">{t('receiptDetails.total')}</div>
                  <div className="text-3xl font-bold text-primary">
                    {formatCurrency(receipt.total, receipt.currency)}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card className="shadow-custom-md border-0">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b">
                <h3 className="text-lg font-semibold">
                  {t('receiptDetails.items', { count: receipt.items.length })}
                </h3>
              </div>

              <div className="space-y-3">
                {receipt.items.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start justify-between p-3 rounded-lg bg-card hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex-1 space-y-1">
                      <div className="font-medium">{item.name}</div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-xs",
                            categoryColors[item.category]
                          )}
                        >
                          {categoryIcons[item.category]} {item.category}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {t('receiptDetails.quantity')}: {item.quantity}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          @ {formatCurrency(item.price, receipt.currency)}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">
                        {formatCurrency(item.amount, receipt.currency)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="pt-4 border-t space-y-2">
                {receipt.subtotal !== undefined && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('receiptDetails.subtotal')}:</span>
                    <span className="font-medium">
                      {formatCurrency(receipt.subtotal, receipt.currency)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold">
                  <span>{t('receiptDetails.total')}:</span>
                  <span className="text-primary">{formatCurrency(receipt.total, receipt.currency)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Receipt Image - Moved to bottom */}
        {receipt.imageUrl && receipt.imageUrl !== '' && (
          <Card className="overflow-hidden">
            <CardContent className="p-4">
              <div className="text-sm font-medium mb-3 text-muted-foreground">{t('receiptDetails.receiptImage')}</div>
              <img
                src={receipt.imageUrl}
                alt={t('receiptDetails.receiptImage')}
                className="w-full h-auto max-h-[600px] object-contain bg-muted rounded-lg"
                onError={(e) => {
                  console.error('Failed to load image:', receipt.imageUrl);
                  e.currentTarget.style.display = 'none';
                }}
              />
            </CardContent>
          </Card>
        )}
        </div>
      </ScrollArea>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm">
          <div className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-50 grid w-full max-w-lg gap-4 border bg-background p-6 shadow-lg rounded-lg">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">{t('receiptDetails.deleteDialog.title')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('receiptDetails.deleteDialog.message')}
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(false)}
                disabled={isDeleting}
              >
                {t('receiptDetails.deleteDialog.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? t('receiptDetails.deleteDialog.deleting') : t('receiptDetails.deleteDialog.delete')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
